/**
 * 주간 보고 화면의 목업 사례.
 * 고른 동안만 이 화면에 겹쳐 보이고, 플랜에는 저장하지 않는다.
 */
import { useSyncExternalStore } from "react";
import type { Project } from "@/engine/types";

export const WEEKLY_MOCK_CASES = [
  { id: "empty", label: "없음" },
  { id: "current", label: "현재 데이터" },
] as const;

export type WeeklyMockCase = (typeof WEEKLY_MOCK_CASES)[number]["id"];

const MOCK_CASE_KEY = "weekly-mock-case";

function readStoredCase(): WeeklyMockCase {
  try {
    const raw = sessionStorage.getItem(MOCK_CASE_KEY);
    if (raw === "empty" || raw === "current") return raw;
  } catch {
    // 이 환경에는 sessionStorage가 없다.
  }
  return "current";
}

let current: WeeklyMockCase = readStoredCase();
const listeners = new Set<() => void>();

export function getWeeklyMockCase(): WeeklyMockCase {
  return current;
}

export function setWeeklyMockCase(next: WeeklyMockCase) {
  if (current === next) return;
  current = next;
  try {
    sessionStorage.setItem(MOCK_CASE_KEY, next);
  } catch {
    // 저장하지 못해도 이번 화면에는 적용한다.
  }
  for (const listener of listeners) listener();
}

export function useWeeklyMockCase(): WeeklyMockCase {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    getWeeklyMockCase,
    getWeeklyMockCase,
  );
}

/** 없음은 마감본과 초안을 가린다. 현재 데이터는 플랜 그대로다. */
export function weeklyProjectForMock<T extends Pick<Project, "weeklySnapshots" | "weeklyDrafts">>(
  project: T,
  scenario: WeeklyMockCase,
): T {
  if (scenario === "current") return project;
  return { ...project, weeklySnapshots: undefined, weeklyDrafts: undefined };
}
